// frontend/src/components/Profile/ProfileDocumentsTab.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Alert, alpha, useTheme
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_BASE = 'https://fincollab.ru/api/auth';

const DocumentsSection = styled(Box)({
  position: 'relative',
});

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0, 1, 0),
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': { height: '8px', background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
  '&:hover::-webkit-scrollbar-thumb': { background: theme.palette.grey[400] },
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.background.paper,
  zIndex: 10,
  '&:hover': { backgroundColor: theme.palette.grey[100] }
}));

const DocumentCard = styled(Box)({
  minWidth: 130,
  maxWidth: 130,
  height: 130,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  padding: 8,
  backgroundColor: 'transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
  }
});

const getFileIcon = (fileType) => {
  const type = (fileType || '').toLowerCase();
  if (type === 'pdf') return <PdfIcon sx={{ fontSize: 52, color: '#e74c3c' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(type))
    return <ImageIcon sx={{ fontSize: 52, color: '#3498db' }} />;
  if (['doc', 'docx'].includes(type)) return <DocIcon sx={{ fontSize: 52, color: '#2980b9' }} />;
  return <FileIcon sx={{ fontSize: 52, color: '#7f8c8d' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ProfileDocumentsTab = ({ isOwnProfile, userId, refreshTrigger }) => {
  const theme = useTheme();
  const scrollRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const displayedDocuments = documents.slice(0, 10);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const url = isOwnProfile
        ? `${API_BASE}/my-documents/`
        : `${API_BASE}/users/${userId}/documents/`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.results || data);
      } else {
        setError('Не удалось загрузить документы');
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки документов:', err);
      setError('Ошибка загрузки документов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [isOwnProfile, userId, refreshTrigger]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const checkScroll = () => {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    };

    checkScroll();
    container.addEventListener('scroll', checkScroll);
    return () => container.removeEventListener('scroll', checkScroll);
  }, [displayedDocuments]);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -250, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 250, behavior: 'smooth' });
  };

  const handleDocumentClick = (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  return (
    <DocumentsSection>
      <Box sx={{ position: 'relative' }}>
        {showLeftArrow && (
          <NavButton onClick={scrollLeft} sx={{ left: -12 }} size="small">
            <ChevronLeftIcon />
          </NavButton>
        )}
        {showRightArrow && (
          <NavButton onClick={scrollRight} sx={{ right: -12 }} size="small">
            <ChevronRightIcon />
          </NavButton>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : documents.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <FolderIcon sx={{ fontSize: 48, color: alpha(theme.palette.primary.main, 0.3), mb: 1 }} />
            <Typography variant="body1" color="primary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
              Нет документов
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isOwnProfile
                ? 'Загрузите сертификаты, грамоты и другие документы'
                : 'Пользователь пока не загрузил документы'}
            </Typography>
          </Box>
        ) : (
          <ScrollContainer ref={scrollRef}>
            {displayedDocuments.map((doc) => (
              <DocumentCard key={doc.id} onClick={() => handleDocumentClick(doc)}>
                {getFileIcon(doc.file_type)}
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '0.65rem',
                    fontWeight: 300,
                    lineHeight: 1.2,
                    color: 'text.secondary',
                  }}
                >
                  {doc.title}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.6rem', fontWeight: 300, color: 'text.disabled' }}>
                  {doc.formatted_size || formatFileSize(doc.file_size)}
                </Typography>
              </DocumentCard>
            ))}
          </ScrollContainer>
        )}
      </Box>
    </DocumentsSection>
  );
};

export default ProfileDocumentsTab;